import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  GoogleMap,
  DirectionsRenderer,
  InfoWindow,
  useJsApiLoader,
  OverlayView,
  Marker,
} from "@react-google-maps/api";
import { FaRegDotCircle } from "react-icons/fa";
import "./map.css";
import { ImOffice } from "react-icons/im";
import { renderToStaticMarkup } from "react-dom/server";
import { getAddressByLatLng } from "./MapFunction";
import { useSelector } from "react-redux";

const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const MAX_WAYPOINTS = 23;

/* ── Bangalore fallback — used whenever coords are missing/invalid ── */
const DEFAULT_CENTER = { lat: 12.985908334627606, lng: 77.58141163898391 };

const isFiniteNum = (v) => typeof v === "number" && Number.isFinite(v);

const isValidLatLng = (c) =>
  c != null && isFiniteNum(c.lat) && isFiniteNum(c.lng);

/** parseFloat that returns null instead of NaN */
const safeFloat = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/** Always returns a valid {lat,lng} — never null, never NaN */
const safeLatLng = (lat, lng) => {
  const la = safeFloat(lat);
  const lo = safeFloat(lng);
  return la !== null && lo !== null ? { lat: la, lng: lo } : DEFAULT_CENTER;
};

const darkModeStyle = [
  { elementType: "geometry",            stylers: [{ color: "#010113" }] },
  { elementType: "labels.icon",         stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill",    stylers: [{ color: "#757575" }] },
  { elementType: "labels.text.stroke",  stylers: [{ color: "#212121" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#757575" }] },
  { featureType: "poi",            elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "road",           elementType: "geometry", stylers: [{ color: "#383838" }] },
  { featureType: "water",          elementType: "geometry", stylers: [{ color: "#000000" }] },
  { featureType: "water",          elementType: "labels.text.fill", stylers: [{ color: "#3d3d3d" }] },
];

const MapWithDynamicPointNames = ({
  center,
  origin,
  destination,
  waypoints,
  radius,
  branchLat,
  branchLng,
  branchName,
}) => {
  const themeStore = useSelector((state) => state.theme);

  /* ── mapCenter MUST start with a valid value — never null ── */
  const [mapCenter, setMapCenter] = useState(() =>
    isValidLatLng(center) ? center : DEFAULT_CENTER
  );
  const [mapZoom, setMapZoom] = useState(15);
  const [isOrigin, setIsOrigin] = useState(false);
  const [directionsResponses, setDirectionsResponses] = useState([]);
  const [branchAddresses, setBranchAddresses] = useState("");
  const [branchAddress, setBranchAddress]   = useState("");
  const [markerAddress, setMarkerAddress]   = useState("");
  const [markerLat, setMarkerLat] = useState(null);
  const [markerLng, setMarkerLng] = useState(null);
  const [isCircle, setIsCircle]   = useState(false);
  const [count, setCount]         = useState(0);

  const mapInstanceRef = useRef(null);
  const geocoderRef    = useRef(null);
  const circleRef      = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const mapContainerStyle = { width: "100%", height: "100%" };
  const iconSVG = renderToStaticMarkup(<ImOffice size={24} color="#FF5722" />);
  const [map, setMap] = useState(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: API_KEY,
  });

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    mapInstanceRef.current = mapInstance;
    geocoderRef.current = new window.google.maps.Geocoder();
    setIsMapLoaded(true);
  }, []);

  /* ── keep mapCenter in sync when center prop changes ── */
  useEffect(() => {
    if (isValidLatLng(center)) {
      setMapCenter({ lat: center.lat, lng: center.lng });
    }
  }, [center]);

  /* ── directions ── */
  const splitWaypoints = (all) => {
    const chunks = [];
    for (let i = 0; i < all.length; i += MAX_WAYPOINTS)
      chunks.push(all.slice(i, i + MAX_WAYPOINTS));
    return chunks;
  };

  useEffect(() => {
    if (!isLoaded) return;
    if (!isValidLatLng(origin) || !isValidLatLng(destination)) {
      setDirectionsResponses([]);
      setIsOrigin(false);
      return;
    }

    const waypointChunks = splitWaypoints(waypoints || []);
    const service = new window.google.maps.DirectionsService();
    const bounds  = new window.google.maps.LatLngBounds();

    const requests = waypointChunks.map((chunk, index) => {
      const chunkOrigin =
        index === 0
          ? { lat: origin.lat, lng: origin.lng }
          : safeLatLng(chunk[0].location.lat, chunk[0].location.lng);

      const chunkDest =
        index === waypointChunks.length - 1
          ? { lat: destination.lat, lng: destination.lng }
          : safeLatLng(
              chunk[chunk.length - 1].location.lat,
              chunk[chunk.length - 1].location.lng
            );

      const chunkWPs =
        index === 0
          ? chunk.slice(0, chunk.length - 1)
          : chunk.slice(1, chunk.length - 1);

      return {
        origin: chunkOrigin,
        destination: chunkDest,
        travelMode: "DRIVING",
        waypoints: chunkWPs,
        optimizeWaypoints: false,
      };
    });

    Promise.all(
      requests.map(
        (req) =>
          new Promise((resolve, reject) => {
            service.route(req, (response, status) => {
              if (status === "OK") {
                bounds.extend(new window.google.maps.LatLng(req.origin.lat, req.origin.lng));
                req.waypoints.forEach((wp) =>
                  bounds.extend(new window.google.maps.LatLng(wp.location.lat, wp.location.lng))
                );
                bounds.extend(new window.google.maps.LatLng(req.destination.lat, req.destination.lng));
                if (mapInstanceRef.current) mapInstanceRef.current.fitBounds(bounds);
                resolve(response);
              } else {
                reject(`Directions failed: ${status}`);
              }
            });
          })
      )
    )
      .then((responses) => {
        setDirectionsResponses(responses);
        setIsOrigin(true);
        if (isValidLatLng(center))
          setMapCenter({ lat: center.lat, lng: center.lng });
        setCount(0);
      })
      .catch(console.error);
  }, [isLoaded, origin, destination, waypoints]);

  /* ── branch circle ── */
  useEffect(() => {
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }

    const bLat = safeFloat(branchLat);
    const bLng = safeFloat(branchLng);
    const r    = parseInt(radius, 10);

    if (isMapLoaded && bLat !== null && bLng !== null && r > 0) {
      setTimeout(() => {
        circleRef.current = new window.google.maps.Circle({
          center:        { lat: bLat, lng: bLng },
          radius:        r,
          map:           mapInstanceRef.current,
          strokeColor:   "#4285F4",
          strokeOpacity: 0.5,
          strokeWeight:  1,
          fillColor:     "rgba(66, 133, 244, 0.3)",
          fillOpacity:   0.3,
        });
        setIsCircle(true);
      }, 300);
    }

    return () => {
      if (circleRef.current) { circleRef.current.setMap(null); circleRef.current = null; }
    };
  }, [radius, branchLat, branchLng, isMapLoaded]);

  /* ── mouse handlers ── */
  const debounce = (fn, delay) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
  };

  const handleMouseOver = async (marker) => {
    const address = await getAddressByLatLng(marker);
    setMarkerLat(marker.lat);
    setMarkerLng(marker.lng);
    setMarkerAddress(address);
  };
  const handleMouseOut = () => { setMarkerLat(null); setMarkerLng(null); setMarkerAddress(""); };

  const debounceMouseOver = useCallback(debounce(handleMouseOver, 100), []);
  const debounceMouseOut  = useCallback(debounce(handleMouseOut, 100), []);

  const getBranchAddress = async (marker) => {
    setBranchAddress(branchName);
    setBranchAddresses(await getAddressByLatLng(marker));
  };
  const resetBranchAddress = () => setBranchAddress("");

  const makeBranchAsCenter = () => {
    const bLat = safeFloat(branchLat);
    const bLng = safeFloat(branchLng);
    if (count === 0 && bLat !== null && bLng !== null) {
      setCount(1);
      setMapCenter({ lat: bLat, lng: bLng });
    } else {
      setCount(0);
      if (isValidLatLng(center)) setMapCenter({ lat: center.lat, lng: center.lng });
    }
  };

  const mapOptions = {
    streetViewControl:   false,
    mapTypeControl:      false,
    minZoom:             5,
    maxZoom:             20,
    zoomControl:         true,
    zoomControlOptions:  { position: 6 },
    fullscreenControl:   true,
    fullscreenControlOptions: { position: 1 },
    draggable:           true,
    scrollwheel:         true,
    disableDefaultUI:    true,
    clickableIcons:      false,
  };

  /* safe branch coords for JSX */
  const bLatSafe = safeFloat(branchLat);
  const bLngSafe = safeFloat(branchLng);
  const branchPos = bLatSafe !== null && bLngSafe !== null
    ? { lat: bLatSafe, lng: bLngSafe }
    : null;

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={mapCenter}               /* ← always a valid {lat,lng} */
      zoom={mapZoom}
      onLoad={onMapLoad}
      options={{
        styles: themeStore.theme === "dark" ? darkModeStyle : null,
        ...mapOptions,
      }}
      onZoomChanged={() => { if (map) setMapZoom(15); }}
    >
      {directionsResponses.map((response, i) => (
        <DirectionsRenderer
          key={i}
          options={{
            directions:     response,
            preserveViewport: true,
            suppressMarkers:  true,
            polylineOptions: { strokeColor: "#019eff", strokeWeight: 5, strokeOpacity: 0.7 },
          }}
        />
      ))}

      {/* Origin marker */}
      {isOrigin && isValidLatLng(origin) && (
        <OverlayView
          position={origin}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h })}
        >
          <div
            className="custom-marker"
            style={{
              transform: "translate(-50%, -100%)",
              borderRadius: "50%",
              background: "linear-gradient(to bottom, lightgray 0%, lightgray 46%, white 46%, white 54%, #fc8702 54%, #fc8702 100%)",
            }}
            onMouseOver={() => debounceMouseOver({ lat: origin.lat, lng: origin.lng })}
            onMouseOut={debounceMouseOut}
          >
            <div className="marker-container">
              <img
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTQEZrATmgHOi5ls0YCCQBTkocia_atSw0X-Q&s"
                alt="origin"
                className="marker-img"
              />
            </div>
            <div className="marker-pointer">
              <FaRegDotCircle style={{ fontSize: "7px", marginLeft: "-2.5px", marginTop: "-7px" }} />
            </div>
          </div>
        </OverlayView>
      )}

      {/* Destination marker */}
      {isOrigin && isValidLatLng(destination) && (
        <OverlayView
          position={destination}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h })}
        >
          <div
            className="custom-marker"
            style={{
              transform: "translate(-50%, -100%)",
              borderRadius: "50%",
              background: "linear-gradient(to bottom, black 0%, black 46%, white 46%, white 54%, #fc8702 54%, #fc8702 100%)",
            }}
            onMouseOver={() => debounceMouseOver({ lat: destination.lat, lng: destination.lng })}
            onMouseOut={debounceMouseOut}
          >
            <div className="marker-container">
              <img
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTQEZrATmgHOi5ls0YCCQBTkocia_atSw0X-Q&s"
                alt="destination"
                className="marker-img"
              />
            </div>
            <div className="marker-pointer">
              <FaRegDotCircle style={{ fontSize: "7px", marginLeft: "-2.5px", marginTop: "-7px" }} />
            </div>
          </div>
        </OverlayView>
      )}

      {/* Branch marker — only when coords are valid */}
      {isCircle && branchPos && (
        <Marker
          position={branchPos}
          icon={{ url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(iconSVG)}` }}
          onMouseOver={() => getBranchAddress(branchPos)}
          onMouseOut={resetBranchAddress}
        />
      )}

      {/* Branch info window */}
      {branchAddress && branchPos && (
        <InfoWindow
          position={branchPos}
          options={{ pixelOffset: new window.google.maps.Size(0, -25) }}
        >
          <div style={{ padding: "10px 10px 10px", borderRadius: "10px", backgroundColor: "#fff", boxShadow: "0 4px 8px rgba(0,0,0,0.2)" }}>
            <h4 style={{ fontSize: "16px", color: "#333", textAlign: "center" }}>📍 {branchAddress}</h4>
            <p  style={{ margin: 0, fontSize: "14px", color: "#555", textAlign: "center" }}>{branchAddresses}</p>
          </div>
        </InfoWindow>
      )}

      {/* Marker hover info window */}
      {markerAddress && markerLat !== null && markerLng !== null && (
        <InfoWindow
          position={{ lat: markerLat, lng: markerLng }}
          options={{ pixelOffset: new window.google.maps.Size(0, -25) }}
        >
          <div style={{ padding: "10px 10px 10px", borderRadius: "10px", backgroundColor: "#fff", boxShadow: "0 4px 8px rgba(0,0,0,0.2)" }}>
            <p style={{ margin: 0, fontSize: "14px", color: "#555", textAlign: "center" }}>{markerAddress}</p>
          </div>
        </InfoWindow>
      )}

      {/* Locate branch / employee toggle */}
      {isValidLatLng(center) && (
        <div className="get-branch-location" onClick={makeBranchAsCenter}>
          {count === 0 ? "Locate Branch" : "Locate Employee"}
        </div>
      )}
    </GoogleMap>
  ) : (
    <></>
  );
};

export default MapWithDynamicPointNames;